/**
 * InviteModal Component
 * Modal for inviting users to a community as Ritualist or Member
 */

import { EllipsisVertical, Loader2, Search, User as UserIcon, X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { UserInvitedIcon } from "@/components/icons/index"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  useCancelInvite,
  useEligibleUsers,
  useInviteUsers,
  useResendInvite,
} from "@/features/communities/hooks/useCommunityInvites"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { cn } from "@/lib"

interface InviteModalProps {
  isOpen: boolean
  onClose: () => void
  communityId: string
  roleType: "ritualist" | "member"
  onSuccess?: () => void
}

interface EligibleUser {
  id: string
  name: string | null
  username: string | null
  profilePictureUrl: string | null
  profile_photos?: Array<{
    id: string
    filePath: string
    isCover: boolean
    is_cover_image: boolean
  }>
  inviteStatus: "none" | "invited"
  inviteId?: string
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: EligibleUser): string | undefined {
  // First try to find cover photo from profile_photos
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
    // If no cover photo, use first photo
    if (user.profile_photos[0]?.filePath) {
      return user.profile_photos[0].filePath
    }
  }
  // Fallback to profilePictureUrl
  return user.profilePictureUrl ?? undefined
}

export function InviteModal({
  isOpen,
  onClose,
  communityId,
  roleType,
  onSuccess,
}: InviteModalProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useEligibleUsers(
    communityId,
    roleType,
    { enabled: isOpen },
  )

  const inviteMutation = useInviteUsers(communityId)
  const cancelInviteMutation = useCancelInvite(communityId)
  const resendInviteMutation = useResendInvite(communityId)

  // Flatten pages into a single array of users
  const users: EligibleUser[] = data?.pages?.flatMap(page => page.users) || []

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("")
      setSelectedUserIds(new Set())
    }
  }, [isOpen])

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      (user.name?.toLowerCase().includes(query) ?? false) ||
      (user.username?.toLowerCase().includes(query) ?? false)
    )
  })

  // Toggle user selection (only for non-invited users)
  const toggleUser = useCallback((userId: string, inviteStatus: string) => {
    // Don't allow selection of already invited users
    if (inviteStatus === "invited") return

    setSelectedUserIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }
      return newSet
    })
  }, [])

  // Handle send invites
  const handleSendInvites = async () => {
    if (selectedUserIds.size === 0) return

    try {
      await inviteMutation.mutateAsync({
        userIds: Array.from(selectedUserIds),
        roleType,
      })
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error("Failed to send invites:", error)
    }
  }

  // Handle cancel invite
  const handleCancelInvite = async (e: React.MouseEvent, inviteId: string) => {
    e.stopPropagation()
    try {
      await cancelInviteMutation.mutateAsync(inviteId)
      onSuccess?.()
    } catch (error) {
      console.error("Failed to cancel invite:", error)
    }
  }

  // Handle resend invite
  const handleResendInvite = async (e: React.MouseEvent, inviteId: string) => {
    e.stopPropagation()
    try {
      await resendInviteMutation.mutateAsync(inviteId)
    } catch (error) {
      console.error("Failed to resend invite:", error)
    }
  }

  // Load more handler
  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      fetchNextPage()
    }
  }, [isLoading, hasNextPage, fetchNextPage])

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: hasNextPage ?? false,
    disabled: isLoading,
  })

  const roleLabel = roleType === "ritualist" ? "Ritualist" : "Member"
  const isPending =
    inviteMutation.isPending || cancelInviteMutation.isPending || resendInviteMutation.isPending

  // Separate invited and non-invited users
  const invitedUsers = filteredUsers.filter(u => u.inviteStatus === "invited")
  const availableUsers = filteredUsers.filter(u => u.inviteStatus === "none")

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="w-[600px] max-h-[80vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-charcoal">
            Invite {roleLabel === "Member" ? "Members" : "Ritualists"}
          </DialogTitle>
        </DialogHeader>

        {/* Search Bar */}
        <div className="px-6 pb-4 mt-5">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <Input
              type="text"
              placeholder="Search by name or username..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 text-sm focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto px-6 min-h-[200px] max-h-[400px]">
          {isLoading && users.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-charcoal"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon size={48} className="mx-auto mb-4 text-stone" />
              <p className="text-stone">
                {searchQuery ? "No users match your search" : "No users available to invite"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Invited Users Section */}
              {invitedUsers.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-stone font-medium mb-2 uppercase tracking-wide">
                    Pending Invites ({invitedUsers.length})
                  </p>
                  {invitedUsers.map(user => {
                    const isResending = resendInviteMutation.isPending
                    const isCancelling = cancelInviteMutation.isPending

                    return (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 p-3 rounded-md transition-colors"
                      >
                        <Avatar
                          src={getUserDisplayImage(user)}
                          fallback={user.name || user.username || "User"}
                          size="md"
                        />

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-charcoal truncate">
                            {user.name || "Unnamed User"}
                          </p>
                          {user.username && (
                            <p className="text-xs text-stone truncate">@{user.username}</p>
                          )}
                        </div>

                        {/* Invited label with hamburger menu */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone flex justify-center items-center gap-1">
                            <UserInvitedIcon className="w-5 h-5" />
                            Invited
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="cursor-pointer p-1 rounded-md hover:bg-stone/10 transition-colors">
                                <EllipsisVertical className="w-4 h-4 text-stone" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={e => user.inviteId && handleResendInvite(e, user.inviteId)}
                                disabled={isResending || !user.inviteId}
                              >
                                Resend
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={e => user.inviteId && handleCancelInvite(e, user.inviteId)}
                                disabled={isCancelling || !user.inviteId}
                              >
                                Withdraw
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Available Users Section */}
              {availableUsers.length > 0 && (
                <div>
                  <p className="text-xs text-stone font-medium mb-2 uppercase tracking-wide">
                    {invitedUsers.length > 0 ? "Available Users" : "Select users to invite"}
                  </p>
                  {availableUsers.map(user => {
                    const isSelected = selectedUserIds.has(user.id)

                    return (
                      <div
                        key={user.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-md transition-colors cursor-pointer",
                          isSelected && "bg-primary/5",
                        )}
                        onClick={() => toggleUser(user.id, user.inviteStatus)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleUser(user.id, user.inviteStatus)}
                          onClick={e => e.stopPropagation()}
                        />

                        <Avatar
                          src={getUserDisplayImage(user)}
                          fallback={user.name || user.username || "User"}
                          size="md"
                        />

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-charcoal truncate">
                            {user.name || "Unnamed User"}
                          </p>
                          {user.username && (
                            <p className="text-xs text-stone truncate">@{user.username}</p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Load more trigger */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="py-4 text-center">
                  {isFetchingNextPage ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-charcoal mx-auto"></div>
                  ) : (
                    <button
                      onClick={handleLoadMore}
                      className="text-sm text-stone hover:text-charcoal"
                    >
                      Load more
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 flex items-center justify-between">
          <p className="text-sm text-stone">
            {selectedUserIds.size > 0
              ? `${selectedUserIds.size} user${selectedUserIds.size === 1 ? "" : "s"} selected`
              : "Select users to invite"}
          </p>

          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={handleSendInvites}
              className="px-10"
              disabled={selectedUserIds.size === 0 || isPending}
            >
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isPending ? "Sending..." : `Send Invites`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
